// Design System Tokens - Generado desde design-system-snowflake.json
// Este archivo mapea los tokens del design system a variables CSS y objetos JavaScript

import designSystemJson from '../design-system-snowflake.json';

const ds = designSystemJson.designSystem;

// Tokens de color
export const colors = {
  primary: {
    blue: ds.colors.primary.blue,
    darkBlue: ds.colors.primary.darkBlue,
    lightBlue: ds.colors.primary.lightBlue,
  },
  neutral: {
    white: ds.colors.neutral.white,
    lightGray: ds.colors.neutral.lightGray,
    mediumGray: ds.colors.neutral.mediumGray,
    darkGray: ds.colors.neutral.darkGray,
    black: ds.colors.neutral.black,
  },
  accent: {
    orange: ds.colors.accent.orange,
    green: ds.colors.accent.green,
  },
  // Estados adicionales para componentes
  states: {
    success: ds.colors.accent.green,
    warning: ds.colors.accent.orange,
    error: '#E53E3E',
    info: ds.colors.primary.blue,
  }
};

// Tokens de color para modo oscuro
export const darkColors = {
  primary: {
    blue: ds.darkMode.colors.primary.blue,
    darkBlue: ds.darkMode.colors.primary.darkBlue,
    lightBlue: ds.darkMode.colors.primary.lightBlue,
  },
  neutral: {
    white: ds.darkMode.colors.neutral.white,
    lightGray: ds.darkMode.colors.neutral.lightGray,
    mediumGray: ds.darkMode.colors.neutral.mediumGray,
    darkGray: ds.darkMode.colors.neutral.darkGray,
    black: ds.darkMode.colors.neutral.black,
  },
  accent: {
    orange: ds.darkMode.colors.accent.orange,
    green: ds.darkMode.colors.accent.green,
  },
  // Estados adicionales para componentes
  states: {
    success: ds.darkMode.colors.accent.green,
    warning: ds.darkMode.colors.accent.orange,
    error: '#E53E3E',
    info: ds.darkMode.colors.primary.blue,
  }
};

// Tokens de modo oscuro
export const darkMode = {
  colors: darkColors,
  backgrounds: ds.darkMode.backgrounds,
  text: ds.darkMode.text,
  borders: ds.darkMode.borders,
  inputs: ds.darkMode.inputs,
};

// Tokens de tipografía
export const typography = {
  fontFamilies: {
    primary: ds.typography.fontFamilies.primary,
    secondary: ds.typography.fontFamilies.secondary,
  },
  fontSizes: {
    xs: ds.typography.fontSizes.xs,
    sm: ds.typography.fontSizes.sm,
    base: ds.typography.fontSizes.base,
    lg: ds.typography.fontSizes.lg,
    xl: ds.typography.fontSizes.xl,
    '2xl': ds.typography.fontSizes['2xl'],
    '3xl': ds.typography.fontSizes['3xl'],
    '4xl': ds.typography.fontSizes['4xl'],
    '5xl': ds.typography.fontSizes['5xl'],
    '6xl': ds.typography.fontSizes['6xl'],
  },
  fontWeights: {
    normal: ds.typography.fontWeights.normal,
    medium: ds.typography.fontWeights.medium,
    semibold: ds.typography.fontWeights.semibold,
    bold: ds.typography.fontWeights.bold,
  },
  lineHeights: {
    tight: ds.typography.lineHeights.tight,
    normal: ds.typography.lineHeights.normal,
    relaxed: ds.typography.lineHeights.relaxed,
  }
};

// Tokens de espaciado
export const spacing = {
  xs: ds.spacing.xs,
  sm: ds.spacing.sm,
  md: ds.spacing.md,
  lg: ds.spacing.lg,
  xl: ds.spacing.xl,
  '2xl': ds.spacing['2xl'],
  '3xl': ds.spacing['3xl'],
  '4xl': ds.spacing['4xl'],
  '5xl': ds.spacing['5xl'],
};

// Tokens de border radius
export const borderRadius = {
  none: ds.borderRadius.none,
  sm: ds.borderRadius.sm,
  md: ds.borderRadius.md,
  lg: ds.borderRadius.lg,
  xl: ds.borderRadius.xl,
  full: ds.borderRadius.full,
};

// Tokens de sombras
export const shadows = {
  sm: ds.shadows.sm,
  md: ds.shadows.md,
  lg: ds.shadows.lg,
  xl: ds.shadows.xl,
};

// Tokens de sombras para modo oscuro
export const darkShadows = {
  sm: ds.darkShadows.sm,
  md: ds.darkShadows.md,
  lg: ds.darkShadows.lg,
  xl: ds.darkShadows.xl,
};

// Tokens de layout
export const layout = {
  maxWidth: ds.layout.maxWidth,
  margins: ds.layout.margins,
  gridColumns: ds.layout.gridColumns,
  gutters: ds.layout.gutters,
  breakpoints: {
    mobile: ds.layout.breakpoints.mobile,
    tablet: ds.layout.breakpoints.tablet,
    desktop: ds.layout.breakpoints.desktop,
  }
};

// Tokens de animación
export const animations = {
  duration: {
    fast: ds.animations.duration.fast,
    normal: ds.animations.duration.normal,
    slow: ds.animations.duration.slow,
  },
  easing: {
    easeIn: ds.animations.easing.easeIn,
    easeOut: ds.animations.easing.easeOut,
    easeInOut: ds.animations.easing.easeInOut,
  }
};

// Componentes del design system
export const components = {
  header: ds.components.header,
  logo: ds.components.logo,
  navigation: ds.components.navigation,
  buttons: ds.components.buttons,
  hero: ds.components.hero,
  headings: ds.components.headings,
  tabs: ds.components.tabs,
  cards: ds.components.cards,
  illustrations: ds.components.illustrations,
};

// Componentes del design system para modo oscuro
export const darkComponents = {
  header: ds.components.darkHeader,
  logo: ds.components.logo,
  navigation: ds.components.darkNavigation,
  buttons: ds.components.darkButtons,
  hero: ds.components.darkHero,
  headings: ds.components.darkHeadings,
  tabs: ds.components.darkTabs,
  cards: ds.components.darkCards,
  illustrations: ds.components.illustrations,
};

// Objeto theme consolidado para uso fácil
export const theme = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  layout,
  animations,
  components,
};

// Objeto theme para modo oscuro
export const darkTheme = {
  colors: darkColors,
  typography,
  spacing,
  borderRadius,
  shadows: darkShadows,
  layout,
  animations,
  components: darkComponents,
  darkMode,
};

// Función para generar variables CSS
export const generateCSSVariables = () => {
  const cssVars = {};
  
  // Colors
  Object.entries(colors.primary).forEach(([key, value]) => {
    cssVars[`--color-primary-${key}`] = value;
  });
  Object.entries(colors.neutral).forEach(([key, value]) => {
    cssVars[`--color-neutral-${key}`] = value;
  });
  Object.entries(colors.accent).forEach(([key, value]) => {
    cssVars[`--color-accent-${key}`] = value;
  });
  Object.entries(colors.states).forEach(([key, value]) => {
    cssVars[`--color-state-${key}`] = value;
  });
  
  // Typography
  Object.entries(typography.fontSizes).forEach(([key, value]) => {
    cssVars[`--font-size-${key}`] = value;
  });
  Object.entries(typography.fontWeights).forEach(([key, value]) => {
    cssVars[`--font-weight-${key}`] = value;
  });
  Object.entries(typography.lineHeights).forEach(([key, value]) => {
    cssVars[`--line-height-${key}`] = value;
  });
  
  // Spacing
  Object.entries(spacing).forEach(([key, value]) => {
    cssVars[`--space-${key}`] = value;
  });
  
  // Border radius
  Object.entries(borderRadius).forEach(([key, value]) => {
    cssVars[`--radius-${key}`] = value;
  });
  
  // Shadows
  Object.entries(shadows).forEach(([key, value]) => {
    cssVars[`--shadow-${key}`] = value;
  });
  
  // Font families
  cssVars['--font-primary'] = typography.fontFamilies.primary;
  cssVars['--font-secondary'] = typography.fontFamilies.secondary;
  
  return cssVars;
};

// Función para generar variables CSS para modo oscuro
export const generateDarkCSSVariables = () => {
  const cssVars = {};
  
  // Dark Colors
  Object.entries(darkColors.primary).forEach(([key, value]) => {
    cssVars[`--color-primary-${key}`] = value;
  });
  Object.entries(darkColors.neutral).forEach(([key, value]) => {
    cssVars[`--color-neutral-${key}`] = value;
  });
  Object.entries(darkColors.accent).forEach(([key, value]) => {
    cssVars[`--color-accent-${key}`] = value;
  });
  Object.entries(darkColors.states).forEach(([key, value]) => {
    cssVars[`--color-state-${key}`] = value;
  });
  
  // Dark Shadows
  Object.entries(darkShadows).forEach(([key, value]) => {
    cssVars[`--shadow-${key}`] = value;
  });
  
  // Dark Mode specific variables
  Object.entries(darkMode.backgrounds).forEach(([key, value]) => {
    cssVars[`--bg-${key}`] = value;
  });
  Object.entries(darkMode.text).forEach(([key, value]) => {
    cssVars[`--text-${key}`] = value;
  });
  Object.entries(darkMode.borders).forEach(([key, value]) => {
    cssVars[`--border-${key}`] = value;
  });
  Object.entries(darkMode.inputs).forEach(([key, value]) => {
    cssVars[`--input-${key}`] = value;
  });
  
  return cssVars;
};

export default theme;
